import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
  Typography,
  Paper,
  Divider,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  TableContainer,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AttachFile from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { utcToDisplayDateAndTime, displayDateTimeToUTC, getTimezoneLabel } from '../../utils/timezoneUtils';
import AMPChecksManager from './AMPChecksManager';
import ScheduleTlbCycleCounterField from '../shared/ScheduleTlbCycleCounterField';
import ScheduleTlbHoursCounterField from '../shared/ScheduleTlbHoursCounterField';

/** When loading from API: for HOURS, value is stored as integer hours. Display as-is (no legacy minutes conversion). */
const normalizeHoursValueFromApi = (recurrenceBasedOn, recurrenceValue) => {
  if (recurrenceBasedOn !== 'HOURS' || recurrenceValue == null) return recurrenceValue;
  const v = Number(recurrenceValue);
  if (Number.isNaN(v)) return recurrenceValue;
  return Math.floor(v);
};

/** Format lastDoneValue from API for form: Date/ISO string → YYYY-MM-DD in display TZ when displayTimezone set; number unchanged (decimals preserved for HOURS). */
const formatLastDoneValueForForm = (recurrenceBasedOn, lastDoneValue, displayTimezone) => {
  if (lastDoneValue == null || lastDoneValue === '') return '';
  if (['DAYS', 'MONTHS', 'YEARS', 'ON_CONDITION'].includes(recurrenceBasedOn)) {
    const iso = typeof lastDoneValue === 'string' ? lastDoneValue : (lastDoneValue instanceof Date ? lastDoneValue.toISOString() : '');
    if (iso && iso.length >= 10) {
      if (displayTimezone) {
        const d = utcToDisplayDateAndTime(iso, displayTimezone).date;
        return d || iso.slice(0, 10);
      }
      return iso.slice(0, 10);
    }
    return '';
  }
  return lastDoneValue;
};

/** Format decimal lastDoneValue (HOURS) for display: e.g. 950.5 → "950h 30m". Empty/null → "". */
const formatLastDoneHoursDisplay = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return '';
  const hours = Math.floor(n);
  let minutes = Math.round((n % 1) * 60);
  if (minutes >= 60) minutes = 59;
  if (minutes < 0) minutes = 0;
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
};

/** Air Conditioning AMP tasks use Hobbs Meter AC for Remaining Hours (not airframe hours). */
const inferAmpHoursTlbKeyFromTitle = (title, description = '') => {
  const hay = `${title || ''} ${description || ''}`.toLowerCase();
  if (/air\s*condition|airconditioning/.test(hay)) return 'hobbsAC';
  return '';
};

/** Last-performed source file metadata from API (upload path is server-managed). */
const normalizeLastDoneSourceDocumentFromApi = (doc) => {
  if (!doc || typeof doc !== 'object' || !doc.fileUrl) return null;
  return {
    fileUrl: doc.fileUrl,
    originalFileName: doc.originalFileName != null ? String(doc.originalFileName) : '',
    mimeType: doc.mimeType != null ? String(doc.mimeType) : '',
    fileSize: typeof doc.fileSize === 'number' ? doc.fileSize : undefined,
    uploadedAt: doc.uploadedAt,
    uploadedBy: doc.uploadedBy
  };
};

const attachLastDoneSourceDocumentToPayloadEntry = (entry, s) => {
  if (s.lastDoneSourceDocument && s.lastDoneSourceDocument.fileUrl) {
    entry.lastDoneSourceDocument = {
      fileUrl: s.lastDoneSourceDocument.fileUrl,
      originalFileName: s.lastDoneSourceDocument.originalFileName || '',
      mimeType: s.lastDoneSourceDocument.mimeType || '',
      fileSize: s.lastDoneSourceDocument.fileSize,
      uploadedAt: s.lastDoneSourceDocument.uploadedAt,
      uploadedBy: s.lastDoneSourceDocument.uploadedBy
    };
  } else {
    entry.lastDoneSourceDocument = null;
  }
};

/** True when schedule row has valid Based On and Value(s) so "Last performed at" has a clear unit. */
const isScheduleRowValid = (s) => {
  if (!s.recurrenceBasedOn) return false;
  if (s.recurrenceBasedOn === 'ON_CONDITION') return (s.recurrenceValue != null && String(s.recurrenceValue).trim() !== '');
  const hasVal1 = s.recurrenceValue !== '' && s.recurrenceValue != null;
  const matchType = s.matchType || 'EXACTLY';
  if (matchType === 'BETWEEN') return hasVal1 && (s.recurrenceValue2 !== '' && s.recurrenceValue2 != null);
  return hasVal1;
};

/**
 * Dialog for creating/editing AMP schedules
 */
const CreateEditAMPScheduleDialog = ({
  open,
  onClose,
  onSuccess,
  schedule,
  aircraft,
  initialAircraftId = '',
  initialInspectionCategory = '',
  lockAircraftAndCategory = false,
  displayTimezone,
  onProofDocumentsChange,
  canUploadProof = true
}) => {
  const [formData, setFormData] = useState({
    aircraftId: '',
    title: '',
    description: '',
    maintenanceType: 'LINE_INSPECTION',
    inspectionCategory: '',
    assemblies: [],
    isNA: false,
    recurrenceType: '',
    schedules: [{ recurrenceBasedOn: '', recurrenceValue: '', matchType: 'EXACTLY', recurrenceValue2: '', tlbCarriedForwardKey: '', lastDoneValue: '', lastDoneNote: '', lastDoneSourceDocument: null }],
    scheduleCombinationMode: 'WHICHEVER_OCCURS_FIRST',
    ampChecks: [],
    adsbBulletinIds: []
  });
  const [adsbOptions, setAdsbOptions] = useState([]);
  const [loadingAdsb, setLoadingAdsb] = useState(false);
  /** Populated AD/SB bulletin objects from the schedule being edited, kept for display even if they fall out of adsbOptions (e.g. after an aircraft change). */
  const [linkedAdsbBulletins, setLinkedAdsbBulletins] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [loadingAssemblies, setLoadingAssemblies] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasWorkOrders, setHasWorkOrders] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyIntimations, setHistoryIntimations] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [lastPerformedDocBusyIndex, setLastPerformedDocBusyIndex] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [deletingProof, setDeletingProof] = useState(null);
  const [currentUsageTotals, setCurrentUsageTotals] = useState(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (!open || !formData.aircraftId) {
      setCurrentUsageTotals(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`/api/aircraft/${formData.aircraftId}/live-usage`)
      .then((res) => {
        if (!cancelled) setCurrentUsageTotals(res.data?.currentUsageTotals || null);
      })
      .catch(() => {
        if (!cancelled) setCurrentUsageTotals(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, formData.aircraftId]);

  useEffect(() => {
    if (open) {
      if (schedule) {
        const inspectionCategory = schedule.inspectionCategory || '';
        const rawRecurrenceType = schedule.recurrenceType || '';
        const recurrenceType =
          rawRecurrenceType === 'ONE_TIME' && inspectionCategory !== 'ONE_TIME_INSPECTIONS'
            ? 'RECURRING'
            : rawRecurrenceType;
        const schedulesList = schedule.schedules && schedule.schedules.length > 0
          ? schedule.schedules.map(s => {
              const inferredHoursTlb =
                s.recurrenceBasedOn === 'HOURS' && !s.tlbCarriedForwardKey
                  ? inferAmpHoursTlbKeyFromTitle(schedule.title, schedule.description)
                  : '';
              return {
              recurrenceBasedOn: s.recurrenceBasedOn || '',
              matchType: s.matchType || 'EXACTLY',
              recurrenceValue: s.recurrenceBasedOn === 'HOURS'
                ? normalizeHoursValueFromApi(s.recurrenceBasedOn, s.recurrenceValue)
                : (s.recurrenceValue ?? ''),
              recurrenceValue2: s.recurrenceBasedOn === 'HOURS' && s.recurrenceValue2 != null
                ? normalizeHoursValueFromApi(s.recurrenceBasedOn, s.recurrenceValue2)
                : (s.recurrenceValue2 ?? ''),
              lastDoneValue: formatLastDoneValueForForm(s.recurrenceBasedOn, s.lastDoneValue, displayTimezone),
              lastDoneNote: s.lastDoneNote != null ? String(s.lastDoneNote) : '',
              tlbCarriedForwardKey: s.tlbCarriedForwardKey || inferredHoursTlb || '',
              lastDoneSourceDocument: normalizeLastDoneSourceDocumentFromApi(s.lastDoneSourceDocument)
            };
            })
          : [{
              recurrenceBasedOn: schedule.recurrenceBasedOn || '',
              matchType: schedule.matchType || 'EXACTLY',
              recurrenceValue: schedule.recurrenceBasedOn === 'HOURS'
                ? normalizeHoursValueFromApi(schedule.recurrenceBasedOn, schedule.recurrenceValue)
                : (schedule.recurrenceValue ?? ''),
              recurrenceValue2: schedule.recurrenceBasedOn === 'HOURS' && schedule.recurrenceValue2 != null
                ? normalizeHoursValueFromApi(schedule.recurrenceBasedOn, schedule.recurrenceValue2)
                : (schedule.recurrenceValue2 ?? ''),
              lastDoneValue: '',
              lastDoneNote: '',
              tlbCarriedForwardKey:
                schedule.recurrenceBasedOn === 'HOURS'
                  ? inferAmpHoursTlbKeyFromTitle(schedule.title, schedule.description)
                  : '',
              lastDoneSourceDocument: null
            }];
        setFormData({
          aircraftId: schedule.aircraftId?._id || schedule.aircraftId || '',
          title: schedule.title || '',
          description: schedule.description || '',
          maintenanceType: schedule.maintenanceType || 'LINE_INSPECTION',
          inspectionCategory,
          assemblies: schedule.assemblies?.map(a => a._id || a) || [],
          isNA: schedule.isNA || false,
          recurrenceType,
          schedules: schedulesList,
          scheduleCombinationMode: schedule.scheduleCombinationMode || 'WHICHEVER_OCCURS_FIRST',
          ampChecks: schedule.ampChecks || [],
          adsbBulletinIds: (schedule.adsbBulletinIds || []).map(b => b?._id || b).filter(Boolean)
        });
        setLinkedAdsbBulletins((schedule.adsbBulletinIds || []).filter(b => b && typeof b === 'object'));
        setHasWorkOrders(schedule.workOrders && schedule.workOrders.length > 0);
        if (schedule.aircraftId?._id || schedule.aircraftId) {
          fetchAssemblies(schedule.aircraftId?._id || schedule.aircraftId);
        }
      } else {
        const aircraftId = initialAircraftId || '';
        const inspectionCategory = initialInspectionCategory || '';
        const isOneTimeCategory = inspectionCategory === 'ONE_TIME_INSPECTIONS';
        setFormData({
          aircraftId,
          title: '',
          description: '',
          maintenanceType: 'LINE_INSPECTION',
          inspectionCategory,
          assemblies: [],
          isNA: false,
          recurrenceType: isOneTimeCategory ? 'ONE_TIME' : 'RECURRING',
          schedules: [{ recurrenceBasedOn: '', recurrenceValue: '', matchType: 'EXACTLY', recurrenceValue2: '', tlbCarriedForwardKey: '', lastDoneValue: '', lastDoneNote: '', lastDoneSourceDocument: null }],
          scheduleCombinationMode: 'WHICHEVER_OCCURS_FIRST',
          ampChecks: [],
          adsbBulletinIds: []
        });
        setLinkedAdsbBulletins([]);
        setHasWorkOrders(false);
        setAssemblies([]);
        if (aircraftId) {
          fetchAssemblies(aircraftId);
        } else {
          setAssemblies([]);
        }
      }
      setError('');
      setTabIndex(0);
    }
  }, [open, schedule, initialAircraftId, initialInspectionCategory]);

  // Fetch history and intimations when History tab is selected
  useEffect(() => {
    if (!open || !schedule || tabIndex !== 1) return;
    const id = schedule._id || schedule.id;
    if (!id) return;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    Promise.all([
      axios.get(`/api/amp-schedules/${id}/history`),
      axios.get('/api/intimations', { params: { sourceType: 'AMP_SCHEDULE', sourceId: String(id) } })
    ]).then(([historyRes, intimationsRes]) => {
      if (cancelled) return;
      setHistoryEvents(Array.isArray(historyRes.data) ? historyRes.data : []);
      setHistoryIntimations(Array.isArray(intimationsRes.data) ? intimationsRes.data : []);
    }).catch((err) => {
      if (!cancelled) setHistoryError(err.response?.data?.message || 'Failed to load history');
    }).finally(() => {
      if (!cancelled) setHistoryLoading(false);
    });
    return () => { cancelled = true; };
  }, [open, schedule, tabIndex]);

  useEffect(() => {
    if (!open || !formData.aircraftId) {
      setAdsbOptions([]);
      return;
    }
    let cancelled = false;
    setLoadingAdsb(true);
    axios
      .get('/api/adsb-service-bulletins', { params: { aircraftId: formData.aircraftId } })
      .then((res) => {
        if (!cancelled) setAdsbOptions(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setAdsbOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAdsb(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, formData.aircraftId]);

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !schedule?._id) return;
    setUploadingProof(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`/api/amp-schedules/${schedule._id}/proof`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      showToast('Proof document uploaded', 'success');
      if (onProofDocumentsChange) onProofDocumentsChange(res.data);
      e.target.value = '';
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleProofDelete = async (fileName) => {
    if (!schedule?._id || !fileName) return;
    setDeletingProof(fileName);
    try {
      const res = await axios.delete(`/api/amp-schedules/${schedule._id}/proof/${encodeURIComponent(fileName)}`);
      showToast('Proof document removed', 'success');
      if (onProofDocumentsChange) onProofDocumentsChange(res.data);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove file', 'error');
    } finally {
      setDeletingProof(null);
    }
  };


  const fetchAssemblies = async (aircraftId) => {
    if (!aircraftId) {
      setAssemblies([]);
      return;
    }
    setLoadingAssemblies(true);
    try {
      const response = await axios.get('/api/assemblies', {
        params: { aircraftId, isActive: 'true' }
      });
      setAssemblies(response.data);
    } catch (error) {
      console.error('Error fetching assemblies:', error);
      showToast('Failed to fetch assemblies', 'error');
    } finally {
      setLoadingAssemblies(false);
    }
  };

  const handleAircraftChange = (e) => {
    const aircraftId = e.target.value;
    setFormData({ ...formData, aircraftId, assemblies: [], adsbBulletinIds: [] });
    setLinkedAdsbBulletins([]);
    fetchAssemblies(aircraftId);
  };

  const handleNAChange = (e) => {
    const isNA = e.target.checked;
    setFormData({
      ...formData,
      isNA,
      assemblies: isNA ? [] : formData.assemblies
    });
  };

  const handleAssembliesChange = (event, newValue) => {
    setFormData({
      ...formData,
      assemblies: newValue.map(assembly => assembly._id || assembly),
      isNA: false
    });
  };

  const handleRecurrenceTypeChange = (e) => {
    setFormData({ ...formData, recurrenceType: e.target.value });
  };

  const handleInspectionCategoryChange = (e) => {
    const inspectionCategory = e.target.value;
    const isOneTimeCategory = inspectionCategory === 'ONE_TIME_INSPECTIONS';
    setFormData({
      ...formData,
      inspectionCategory,
      recurrenceType: isOneTimeCategory ? 'ONE_TIME' : 'RECURRING'
    });
  };

  const handleAddSchedule = () => {
    setFormData({
      ...formData,
      schedules: [...formData.schedules, { recurrenceBasedOn: '', recurrenceValue: '', matchType: 'EXACTLY', recurrenceValue2: '', tlbCarriedForwardKey: '', lastDoneValue: '', lastDoneNote: '', lastDoneSourceDocument: null }]
    });
  };

  const handleRemoveSchedule = (index) => {
    if (formData.schedules.length <= 1) return;
    setFormData({
      ...formData,
      schedules: formData.schedules.filter((_, i) => i !== index)
    });
  };

  const handleScheduleChange = (index, field, value) => {
    const next = formData.schedules.map((s, i) =>
      i === index ? { ...s, [field]: value } : s
    );
    setFormData({ ...formData, schedules: next });
  };

  const handleLastPerformedSourceUpload = async (scheduleIndex, fileList) => {
    const file = fileList && fileList[0];
    const sid = schedule?._id || schedule?.id;
    if (!file || !sid) return;
    setLastPerformedDocBusyIndex(scheduleIndex);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('scheduleIndex', String(scheduleIndex));
      const { data } = await axios.post(`/api/amp-schedules/${sid}/last-performed-document`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const doc = data.schedules?.[scheduleIndex]?.lastDoneSourceDocument;
      const normalized = normalizeLastDoneSourceDocumentFromApi(doc);
      setFormData((prev) => ({
        ...prev,
        schedules: prev.schedules.map((row, i) =>
          i === scheduleIndex ? { ...row, lastDoneSourceDocument: normalized } : row
        )
      }));
      showToast('Source document uploaded', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setLastPerformedDocBusyIndex(null);
    }
  };

  const handleLastPerformedSourceRemove = async (scheduleIndex) => {
    const sid = schedule?._id || schedule?.id;
    if (!sid) return;
    setLastPerformedDocBusyIndex(scheduleIndex);
    try {
      await axios.delete(`/api/amp-schedules/${sid}/last-performed-document/${scheduleIndex}`);
      setFormData((prev) => ({
        ...prev,
        schedules: prev.schedules.map((row, i) =>
          i === scheduleIndex ? { ...row, lastDoneSourceDocument: null } : row
        )
      }));
      showToast('Source document removed', 'success');
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || 'Failed to remove document', 'error');
    } finally {
      setLastPerformedDocBusyIndex(null);
    }
  };

  const handleSubmit = async () => {
    setError('');
    
    // Validation
    if (!formData.aircraftId) {
      setError('Aircraft is required');
      return;
    }
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!formData.maintenanceType) {
      setError('Environment is required');
      return;
    }
    if (!formData.inspectionCategory) {
      setError('Inspection Category is required');
      return;
    }
    if (formData.ampChecks.length === 0) {
      setError('At least one AMP check is required');
      return;
    }
    if (formData.isNA && formData.assemblies.length > 0) {
      setError('If "NA" is selected, assemblies must be empty');
      return;
    }
    const needsRecurrenceConfig =
      formData.recurrenceType === 'RECURRING' || formData.inspectionCategory === 'ONE_TIME_INSPECTIONS';
    if (needsRecurrenceConfig) {
      for (let i = 0; i < formData.schedules.length; i++) {
        const s = formData.schedules[i];
        if (!s.recurrenceBasedOn) {
          setError('Each schedule must have "Based On" selected');
          return;
        }
        if (s.recurrenceBasedOn !== 'ON_CONDITION' && (s.recurrenceValue !== 0 && s.recurrenceValue !== '0' && !s.recurrenceValue)) {
          setError('Each schedule must have "Based On" and "Value"');
          return;
        }
        const matchType = s.matchType || 'EXACTLY';
        if (matchType === 'BETWEEN' && s.recurrenceBasedOn !== 'ON_CONDITION') {
          if (s.recurrenceValue2 !== 0 && s.recurrenceValue2 !== '0' && (s.recurrenceValue2 == null || s.recurrenceValue2 === '')) {
            setError('For "Between", both Value 1 and Value 2 are required');
            return;
          }
        }
        if (s.recurrenceBasedOn === 'HOURS') {
          const hours = typeof s.recurrenceValue === 'number' ? Math.floor(s.recurrenceValue) : parseInt(String(s.recurrenceValue), 10);
          if (Number.isNaN(hours) || hours < 0 || !Number.isInteger(Number(s.recurrenceValue))) {
            setError('Hours value must be a whole number (e.g. 1500)');
            return;
          }
          if (matchType === 'BETWEEN') {
            const hours2 = typeof s.recurrenceValue2 === 'number' ? Math.floor(s.recurrenceValue2) : parseInt(String(s.recurrenceValue2), 10);
            if (Number.isNaN(hours2) || hours2 < 0 || !Number.isInteger(Number(s.recurrenceValue2))) {
              setError('Hours Value 2 must be a whole number');
              return;
            }
            if (hours > hours2) {
              setError('Value 1 must be less than or equal to Value 2');
              return;
            }
          }
        } else if (s.recurrenceBasedOn === 'CYCLES') {
          const num = typeof s.recurrenceValue === 'number' ? s.recurrenceValue : parseFloat(s.recurrenceValue);
          if (Number.isNaN(num) || num < 0) {
            setError('Cycles value must be a number (e.g. 123456.123)');
            return;
          }
          if (matchType === 'BETWEEN') {
            const num2 = typeof s.recurrenceValue2 === 'number' ? s.recurrenceValue2 : parseFloat(s.recurrenceValue2);
            if (Number.isNaN(num2) || num2 < 0) {
              setError('Cycles Value 2 must be a number');
              return;
            }
            if (num > num2) {
              setError('Value 1 must be less than or equal to Value 2');
              return;
            }
          }
        } else if (['YEARS', 'DAYS', 'MONTHS', 'LANDINGS'].includes(s.recurrenceBasedOn)) {
          const num = parseInt(s.recurrenceValue, 10);
          if (Number.isNaN(num) || num < 1 || !Number.isInteger(Number(s.recurrenceValue))) {
            setError('Years, Days, Months and Landings must be whole numbers (no decimals)');
            return;
          }
          if (matchType === 'BETWEEN') {
            const num2 = parseInt(s.recurrenceValue2, 10);
            if (Number.isNaN(num2) || num2 < 0 || !Number.isInteger(Number(s.recurrenceValue2))) {
              setError('Value 2 must be a whole number');
              return;
            }
            if (num > num2) {
              setError('Value 1 must be less than or equal to Value 2');
              return;
            }
          }
        }
        if (s.recurrenceBasedOn === 'HOURS' && s.lastDoneValue !== '' && s.lastDoneValue != null) {
          const minutes = Math.round((Number(s.lastDoneValue) % 1) * 60);
          if (minutes < 0 || minutes > 59) {
            setError('Minutes must be between 0 and 59');
            return;
          }
        }
      }
    }

    setLoading(true);
    try {
      const schedulesPayload = formData.schedules.map(s => {
        const matchType = s.matchType || 'EXACTLY';
        if (s.recurrenceBasedOn === 'ON_CONDITION') {
          const text = (s.recurrenceValue != null && String(s.recurrenceValue).trim() !== '')
            ? String(s.recurrenceValue).trim()
            : 'Based on condition';
          const entry = { recurrenceBasedOn: s.recurrenceBasedOn, matchType: 'EXACTLY', recurrenceValue: text };
          const ldv = s.lastDoneValue;
          if (ldv != null && String(ldv).trim() && String(ldv).length >= 10) {
            const dateStr = String(ldv).trim().slice(0, 10);
            const iso = displayTimezone ? displayDateTimeToUTC(dateStr, '00:00', displayTimezone) : null;
            if (iso) {
              entry.lastDoneValue = new Date(iso).toISOString();
            } else {
              const d = new Date(ldv);
              if (!Number.isNaN(d.getTime())) entry.lastDoneValue = d.toISOString();
            }
          }
          if (s.lastDoneNote != null && String(s.lastDoneNote).trim()) entry.lastDoneNote = String(s.lastDoneNote).trim();
          attachLastDoneSourceDocumentToPayloadEntry(entry, s);
          return entry;
        }
        let value;
        if (s.recurrenceBasedOn === 'HOURS') {
          value = typeof s.recurrenceValue === 'number' ? Math.floor(s.recurrenceValue) : parseInt(String(s.recurrenceValue), 10);
          if (Number.isNaN(value) || value < 0) value = 0;
        } else if (s.recurrenceBasedOn === 'CYCLES') {
          value = typeof s.recurrenceValue === 'number' ? s.recurrenceValue : parseFloat(s.recurrenceValue, 10);
          if (Number.isNaN(value)) value = 0;
        } else {
          value = parseInt(s.recurrenceValue, 10);
          if (Number.isNaN(value)) value = 0;
        }
        const entry = { recurrenceBasedOn: s.recurrenceBasedOn, matchType, recurrenceValue: value };
        if (matchType === 'BETWEEN') {
          let value2;
          if (s.recurrenceBasedOn === 'HOURS') {
            value2 = typeof s.recurrenceValue2 === 'number' ? Math.floor(s.recurrenceValue2) : parseInt(String(s.recurrenceValue2), 10);
            if (Number.isNaN(value2) || value2 < 0) value2 = value;
          } else if (s.recurrenceBasedOn === 'CYCLES') {
            value2 = typeof s.recurrenceValue2 === 'number' ? s.recurrenceValue2 : parseFloat(s.recurrenceValue2, 10);
            if (Number.isNaN(value2)) value2 = value;
          } else {
            value2 = parseInt(s.recurrenceValue2, 10);
            if (Number.isNaN(value2)) value2 = value;
          }
          entry.recurrenceValue2 = value2;
        }
        // Last performed at (optional)
        if (['HOURS', 'CYCLES', 'LANDINGS'].includes(s.recurrenceBasedOn)) {
          if (s.lastDoneValue !== '' && s.lastDoneValue != null) {
            if (s.recurrenceBasedOn === 'CYCLES') {
              const v = parseFloat(s.lastDoneValue);
              if (!Number.isNaN(v) && v >= 0) entry.lastDoneValue = v;
            } else if (s.recurrenceBasedOn === 'HOURS') {
              const v = Number(s.lastDoneValue);
              if (!Number.isNaN(v) && v >= 0) entry.lastDoneValue = v;
            } else {
              const v = parseInt(s.lastDoneValue, 10);
              if (!Number.isNaN(v) && v >= 0) entry.lastDoneValue = Math.floor(Number(s.lastDoneValue));
            }
          }
          if (s.lastDoneNote != null && String(s.lastDoneNote).trim()) entry.lastDoneNote = String(s.lastDoneNote).trim();
        } else if (['DAYS', 'MONTHS', 'YEARS'].includes(s.recurrenceBasedOn)) {
          if (s.lastDoneValue && String(s.lastDoneValue).trim().length >= 10) {
            const dateStr = String(s.lastDoneValue).trim().slice(0, 10);
            const iso = displayTimezone ? displayDateTimeToUTC(dateStr, '00:00', displayTimezone) : null;
            entry.lastDoneValue = iso ? new Date(iso).toISOString() : new Date(s.lastDoneValue).toISOString();
          }
          if (s.lastDoneNote != null && String(s.lastDoneNote).trim()) entry.lastDoneNote = String(s.lastDoneNote).trim();
        }
        attachLastDoneSourceDocumentToPayloadEntry(entry, s);
        if ((s.recurrenceBasedOn === 'CYCLES' || s.recurrenceBasedOn === 'HOURS') && s.tlbCarriedForwardKey) {
          entry.tlbCarriedForwardKey = String(s.tlbCarriedForwardKey).trim();
        }
        return entry;
      });
      const firstPayload = schedulesPayload[0];
      const payload = {
        ...formData,
        schedules: schedulesPayload,
        scheduleCombinationMode: formData.scheduleCombinationMode,
        recurrenceType: formData.inspectionCategory === 'ONE_TIME_INSPECTIONS' ? 'ONE_TIME' : formData.recurrenceType,
        recurrenceBasedOn: firstPayload?.recurrenceBasedOn || formData.schedules[0]?.recurrenceBasedOn,
        matchType: firstPayload?.matchType || 'EXACTLY',
        recurrenceValue: firstPayload?.recurrenceValue ?? null,
        recurrenceValue2: firstPayload?.matchType === 'BETWEEN' ? firstPayload?.recurrenceValue2 ?? null : null
      };

      if (schedule) {
        // Update
        const response = await axios.put(`/api/amp-schedules/${schedule._id}`, payload);
        if (response.data.hasWorkOrders) {
          showToast('AMP schedule updated. Note: Existing work orders will not reflect changes.', 'warning');
        } else {
          showToast('AMP schedule updated successfully', 'success');
        }
      } else {
        // Create
        await axios.post('/api/amp-schedules', payload);
        showToast('AMP schedule created successfully', 'success');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving AMP schedule:', error);
      setError(error.response?.data?.message || 'Failed to save AMP schedule');
      showToast(error.response?.data?.message || 'Failed to save AMP schedule', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {schedule ? 'Edit AMP Schedule' : 'Create AMP Schedule'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {hasWorkOrders && schedule && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This AMP schedule has {schedule.workOrders?.length ?? 0} work order(s) already created.
            Changes to this schedule will not be reflected in existing work orders.
          </Alert>
        )}
        {schedule && (
          <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
            <Tab label="Schedule" />
            <Tab label="History" />
          </Tabs>
        )}
        {(tabIndex === 0 || !schedule) && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Basic Information Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    select
                    label="Aircraft *"
                    value={formData.aircraftId}
                    onChange={handleAircraftChange}
                    required
                    disabled={lockAircraftAndCategory}
                    sx={{ minWidth: 150 }}
                  >
                    {aircraft.map((ac) => (
                      <MenuItem key={ac._id} value={ac._id}>
                        {ac.registration} - {ac.manufacturer} {ac.model}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Environment *"
                    value={formData.maintenanceType}
                    onChange={(e) => setFormData({ ...formData, maintenanceType: e.target.value })}
                    required
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="LINE_INSPECTION">Line</MenuItem>
                    <MenuItem value="BASE_INSPECTION">Base</MenuItem>
                    <MenuItem value="ANY">Any</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Inspection Category *"
                    value={formData.inspectionCategory}
                    onChange={handleInspectionCategoryChange}
                    required
                    disabled={lockAircraftAndCategory}
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="PHASE_INSPECTION">Phase Inspection</MenuItem>
                    <MenuItem value="OUT_OF_PHASE_INSPECTION">Out of phase Inspection</MenuItem>
                    <MenuItem value="LUBRICATION_INSPECTION">Lubrication Inspection</MenuItem>
                    <MenuItem value="CONDITIONAL_INSPECTION">Conditional Inspection</MenuItem>
                    <MenuItem value="ONE_TIME_INSPECTIONS">One Time Inspections</MenuItem>
                    <MenuItem value="CORROSION_PREVENTION_AND_CONTROL">Corrosion Prevention and Control</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title *"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter a detailed description of this AMP schedule..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={adsbOptions}
                    value={formData.adsbBulletinIds.map((id) =>
                      adsbOptions.find((b) => String(b._id) === String(id)) ||
                      linkedAdsbBulletins.find((b) => String(b._id) === String(id)) ||
                      { _id: id }
                    )}
                    onChange={(_, newValue) =>
                      setFormData({
                        ...formData,
                        adsbBulletinIds: newValue.map((b) => b._id)
                      })
                    }
                    disabled={!formData.aircraftId || loadingAdsb}
                    getOptionLabel={(b) => {
                      const num = b.adsbBulletinNumber || '';
                      const t = b.title || '';
                      const parts = [num, t].filter(Boolean);
                      return parts.length ? parts.join(' — ') : String(b._id || '');
                    }}
                    isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
                    renderTags={(value, getTagProps) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {value.map((option, index) => (
                          <Chip
                            key={option._id}
                            label={option.adsbBulletinNumber || option.title || String(option._id)}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))}
                      </Box>
                    )}
                    renderOption={(props, option, { selected }) => (
                      <Box component="li" {...props} key={option._id}>
                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {option.adsbBulletinNumber || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.title || ''}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Linked AD/SB (optional)"
                        placeholder={loadingAdsb ? 'Loading…' : 'Bulletins that include this aircraft'}
                        helperText="Work orders created from this AMP schedule will reference these bulletins for traceability. AD/SB recurrence is not advanced from the AMP path."
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Assembly Configuration Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                Assembly Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isNA}
                        onChange={handleNAChange}
                      />
                    }
                    label="NA (Not Applicable)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={assemblies}
                    value={assemblies.filter(assembly => {
                      const assemblyId = assembly._id?.toString() || assembly._id;
                      return formData.assemblies.some(id => 
                        id?.toString() === assemblyId || id === assemblyId
                      );
                    })}
                    onChange={handleAssembliesChange}
                    disabled={formData.isNA || loadingAssemblies || !formData.aircraftId}
                    getOptionLabel={(option) => 
                      `${option.typeName || 'Unknown'} - SN: ${option.serialNumber || 'N/A'} - Pos: ${option.position || 'N/A'}`
                    }
                    isOptionEqualToValue={(option, value) => {
                      const optionId = option._id?.toString() || option._id;
                      const valueId = value._id?.toString() || value._id;
                      return optionId === valueId;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assemblies"
                        placeholder="Search and select assemblies..."
                        sx={{ minWidth: 150 }}
                      />
                    )}
                    renderTags={(value, getTagProps) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {value.map((option, index) => (
                          <Chip
                            key={option._id}
                            label={option.typeName || 'Unknown'}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))}
                      </Box>
                    )}
                    renderOption={(props, option, { selected }) => (
                      <Box component="li" {...props} key={option._id}>
                        <Checkbox checked={selected} sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {option.typeName || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            SN: {option.serialNumber || 'N/A'} - Pos: {option.position || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    ListboxProps={{
                      style: {
                        maxHeight: 400
                      }
                    }}
                    PaperComponent={({ children, ...other }) => (
                      <Paper
                        {...other}
                        sx={{
                          minWidth: 500,
                          maxHeight: 400
                        }}
                      >
                        {children}
                      </Paper>
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Schedule Configuration Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                Schedule Configuration
              </Typography>
              <Grid container spacing={2}>
                {(formData.inspectionCategory === 'ONE_TIME_INSPECTIONS' || formData.recurrenceType === 'RECURRING') && (
                  <>
                    {formData.inspectionCategory === 'ONE_TIME_INSPECTIONS' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Recurrence"
                          value="Only Once"
                          InputProps={{ readOnly: true }}
                          sx={{ maxWidth: 200 }}
                        />
                      </Grid>
                    )}
                    {formData.recurrenceType === 'RECURRING' && formData.inspectionCategory !== 'ONE_TIME_INSPECTIONS' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Schedule Type"
                          value="Recurring"
                          InputProps={{ readOnly: true }}
                          sx={{ maxWidth: 200 }}
                        />
                      </Grid>
                    )}
                    {formData.schedules.some(e => e.recurrenceBasedOn === 'ON_CONDITION') && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ py: 0.5 }}>
                          <strong>On Condition</strong> means the task is due when a specific condition is met (e.g. wear limits, damage found, or other inspection findings), rather than at a fixed interval. It may be one-time or recurring—typically recurring when the condition can occur again.
                        </Alert>
                      </Grid>
                    )}
                    {formData.schedules.map((entry, index) => (
                      <Grid container spacing={2} key={index} alignItems="center" item xs={12}>
                        <Grid item xs={12} md={3}>
                          <FormControl fullWidth required sx={{ minWidth: 120 }}>
                            <InputLabel>Based On *</InputLabel>
                            <Select
                              value={entry.recurrenceBasedOn}
                              label="Based On *"
                              onChange={(e) => {
                                const nextBasedOn = e.target.value;
                                const defaultVal = nextBasedOn === 'ON_CONDITION' ? 'Based on condition' : '';
                                const prevBasedOn = entry.recurrenceBasedOn;
                                const keepTlbKey =
                                  (nextBasedOn === 'CYCLES' && prevBasedOn === 'CYCLES') ||
                                  (nextBasedOn === 'HOURS' && prevBasedOn === 'HOURS');
                                const next = formData.schedules.map((s, i) =>
                                  i === index
                                    ? {
                                        ...s,
                                        recurrenceBasedOn: nextBasedOn,
                                        recurrenceValue: defaultVal,
                                        ...(nextBasedOn === 'ON_CONDITION' ? { matchType: 'EXACTLY', recurrenceValue2: '' } : {}),
                                        ...(!keepTlbKey ? { tlbCarriedForwardKey: '' } : {})
                                      }
                                    : s
                                );
                                setFormData({ ...formData, schedules: next });
                              }}
                              required
                            >
                              <MenuItem value="HOURS">Hours</MenuItem>
                              <MenuItem value="CYCLES">Cycles</MenuItem>
                              <MenuItem value="LANDINGS">Landings</MenuItem>
                              <MenuItem value="DAYS">Days</MenuItem>
                              <MenuItem value="MONTHS">Months</MenuItem>
                              <MenuItem value="YEARS">Years</MenuItem>
                              <MenuItem value="ON_CONDITION">On Condition</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {entry.recurrenceBasedOn === 'CYCLES' && (
                          <Grid item xs={12}>
                            <ScheduleTlbCycleCounterField
                              aircraftId={formData.aircraftId}
                              currentUsageTotals={currentUsageTotals}
                              value={entry.tlbCarriedForwardKey || ''}
                              onChange={(key) => handleScheduleChange(index, 'tlbCarriedForwardKey', key)}
                              dialogOpen={open}
                            />
                          </Grid>
                        )}
                        {entry.recurrenceBasedOn === 'HOURS' && (
                          <Grid item xs={12}>
                            <ScheduleTlbHoursCounterField
                              aircraftId={formData.aircraftId}
                              currentUsageTotals={currentUsageTotals}
                              value={entry.tlbCarriedForwardKey || ''}
                              onChange={(key) => handleScheduleChange(index, 'tlbCarriedForwardKey', key)}
                              dialogOpen={open}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth size="small" sx={{ minWidth: 100 }}>
                            <InputLabel>Matches</InputLabel>
                            <Select
                              value={entry.recurrenceBasedOn === 'ON_CONDITION' ? 'EXACTLY' : (entry.matchType || 'EXACTLY')}
                              label="Matches"
                              onChange={(e) => {
                                const nextMatch = e.target.value;
                                const next = formData.schedules.map((s, i) =>
                                  i === index
                                    ? { ...s, matchType: nextMatch, ...(nextMatch === 'EXACTLY' ? { recurrenceValue2: '' } : {}) }
                                    : s
                                );
                                setFormData({ ...formData, schedules: next });
                              }}
                              disabled={entry.recurrenceBasedOn === 'ON_CONDITION'}
                            >
                              <MenuItem value="EXACTLY">Exactly</MenuItem>
                              <MenuItem value="BETWEEN">Between</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {entry.matchType === 'BETWEEN' && entry.recurrenceBasedOn !== 'ON_CONDITION' ? (
                          <>
                            <Grid item xs={12} md={2} key={`schedule-v1-${index}`}>
                              {entry.recurrenceBasedOn === 'HOURS' ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Value 1 *"
                                  type="number"
                                  value={entry.recurrenceValue === '' || entry.recurrenceValue == null ? '' : (Number.isNaN(Number(entry.recurrenceValue)) ? '' : Math.floor(Number(entry.recurrenceValue)))}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '') { handleScheduleChange(index, 'recurrenceValue', ''); return; }
                                    const num = parseInt(v, 10);
                                    if (!Number.isNaN(num) && num >= 0) handleScheduleChange(index, 'recurrenceValue', num);
                                  }}
                                  placeholder="1500"
                                  inputProps={{ min: 0, step: 1 }}
                                />
                              ) : entry.recurrenceBasedOn === 'CYCLES' ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Value 1 *"
                                  value={entry.recurrenceValue === '' ? '' : entry.recurrenceValue}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === '') { handleScheduleChange(index, 'recurrenceValue', ''); return; }
                                    const allowed = raw.replace(/[^0-9.]/g, '');
                                    const parts = allowed.split('.');
                                    const intPart = (parts[0] || '').slice(0, 8);
                                    const decPart = parts.length > 1 ? (parts.slice(1).join('').slice(0, 3)) : '';
                                    handleScheduleChange(index, 'recurrenceValue', decPart ? `${intPart}.${decPart}` : intPart);
                                  }}
                                  placeholder="123456.123"
                                />
                              ) : (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Value 1 *"
                                  type="number"
                                  value={entry.recurrenceValue === '' || entry.recurrenceValue == null ? '' : (Number.isNaN(Number(entry.recurrenceValue)) ? '' : Math.floor(Number(entry.recurrenceValue)))}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '') { handleScheduleChange(index, 'recurrenceValue', ''); return; }
                                    const num = parseInt(v, 10);
                                    if (!Number.isNaN(num) && num >= 0) handleScheduleChange(index, 'recurrenceValue', num);
                                  }}
                                  inputProps={{ min: 0, step: 1 }}
                                />
                              )}
                            </Grid>
                            <Grid item xs={12} md={2} key={`schedule-v2-${index}`}>
                              {entry.recurrenceBasedOn === 'HOURS' ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Value 2 *"
                                  type="number"
                                  value={entry.recurrenceValue2 === '' || entry.recurrenceValue2 == null ? '' : (Number.isNaN(Number(entry.recurrenceValue2)) ? '' : Math.floor(Number(entry.recurrenceValue2)))}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '') { handleScheduleChange(index, 'recurrenceValue2', ''); return; }
                                    const num = parseInt(v, 10);
                                    if (!Number.isNaN(num) && num >= 0) handleScheduleChange(index, 'recurrenceValue2', num);
                                  }}
                                  placeholder="1500"
                                  inputProps={{ min: 0, step: 1 }}
                                />
                              ) : entry.recurrenceBasedOn === 'CYCLES' ? (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Value 2 *"
                                  value={entry.recurrenceValue2 === '' ? '' : entry.recurrenceValue2}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === '') { handleScheduleChange(index, 'recurrenceValue2', ''); return; }
                                    const allowed = raw.replace(/[^0-9.]/g, '');
                                    const parts = allowed.split('.');
                                    const intPart = (parts[0] || '').slice(0, 8);
                                    const decPart = parts.length > 1 ? (parts.slice(1).join('').slice(0, 3)) : '';
                                    handleScheduleChange(index, 'recurrenceValue2', decPart ? `${intPart}.${decPart}` : intPart);
                                  }}
                                  placeholder="123456.123"
                                />
                              ) : (
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Value 2 *"
                                  type="number"
                                  value={entry.recurrenceValue2 === '' || entry.recurrenceValue2 == null ? '' : (Number.isNaN(Number(entry.recurrenceValue2)) ? '' : Math.floor(Number(entry.recurrenceValue2)))}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '') { handleScheduleChange(index, 'recurrenceValue2', ''); return; }
                                    const num = parseInt(v, 10);
                                    if (!Number.isNaN(num) && num >= 0) handleScheduleChange(index, 'recurrenceValue2', num);
                                  }}
                                  inputProps={{ min: 0, step: 1 }}
                                />
                              )}
                            </Grid>
                          </>
                        ) : (
                          <Grid item xs={12} md={4} key={`schedule-value-${index}-${entry.recurrenceBasedOn}`}>
                            {entry.recurrenceBasedOn === 'HOURS' ? (
                              <TextField
                                fullWidth
                                label="Value *"
                                type="number"
                                value={entry.recurrenceValue === '' || entry.recurrenceValue == null ? '' : (Number.isNaN(Number(entry.recurrenceValue)) ? '' : Math.floor(Number(entry.recurrenceValue)))}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === '') {
                                    handleScheduleChange(index, 'recurrenceValue', '');
                                    return;
                                  }
                                  const num = parseInt(v, 10);
                                  if (!Number.isNaN(num) && num >= 0) handleScheduleChange(index, 'recurrenceValue', num);
                                }}
                                placeholder="1500"
                                inputProps={{ min: 0, step: 1 }}
                                helperText="Hours (integer)"
                                required
                              />
                            ) : entry.recurrenceBasedOn === 'CYCLES' ? (
                              <Tooltip title="e.g. 123456.123" placement="top" enterDelay={300} arrow>
                                <Box sx={{ width: '100%' }}>
                                  <TextField
                                    fullWidth
                                    label="Value *"
                                    value={entry.recurrenceValue === '' ? '' : entry.recurrenceValue}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      if (raw === '') {
                                        handleScheduleChange(index, 'recurrenceValue', '');
                                        return;
                                      }
                                      const allowed = raw.replace(/[^0-9.]/g, '');
                                      const parts = allowed.split('.');
                                      const intPart = (parts[0] || '').slice(0, 8);
                                      const decPart = parts.length > 1 ? (parts.slice(1).join('').slice(0, 3)) : '';
                                      const out = decPart ? `${intPart}.${decPart}` : intPart;
                                      handleScheduleChange(index, 'recurrenceValue', out);
                                    }}
                                    placeholder="123456.123"
                                    helperText="Up to 8 digits, decimal, 3 decimal places"
                                    required
                                  />
                                </Box>
                              </Tooltip>
                            ) : (['YEARS', 'DAYS', 'MONTHS', 'LANDINGS'].includes(entry.recurrenceBasedOn)) ? (
                              <TextField
                                fullWidth
                                label="Value *"
                                type="number"
                                value={entry.recurrenceValue === '' || entry.recurrenceValue == null ? '' : (Number.isNaN(Number(entry.recurrenceValue)) ? '' : Math.floor(Number(entry.recurrenceValue)))}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === '') {
                                    handleScheduleChange(index, 'recurrenceValue', '');
                                    return;
                                  }
                                  const num = parseInt(v, 10);
                                  if (!Number.isNaN(num) && num >= 0) {
                                    handleScheduleChange(index, 'recurrenceValue', num);
                                  }
                                }}
                                inputProps={{ min: 0, step: 1 }}
                                required
                              />
                            ) : entry.recurrenceBasedOn === 'ON_CONDITION' ? (
                              <TextField
                                fullWidth
                                label="Value"
                                value={entry.recurrenceValue == null || entry.recurrenceValue === '' ? '' : String(entry.recurrenceValue)}
                                onChange={(e) => handleScheduleChange(index, 'recurrenceValue', e.target.value)}
                                placeholder="Based on condition"
                                helperText="Free text describing the condition (e.g. when wear limits are reached)"
                              />
                            ) : (
                              <TextField
                                fullWidth
                                label="Value *"
                                type="number"
                                value={entry.recurrenceValue}
                                onChange={(e) => handleScheduleChange(index, 'recurrenceValue', e.target.value)}
                                inputProps={{ min: 1 }}
                                required
                              />
                            )}
                          </Grid>
                        )}
                        <Grid item xs={12} md={2}>
                          {formData.schedules.length > 1 ? (
                            <IconButton
                              color="error"
                              onClick={() => handleRemoveSchedule(index)}
                              aria-label="Remove schedule"
                              size="small"
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          ) : null}
                        </Grid>
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddSchedule}
                        variant="outlined"
                        size="small"
                      >
                        Add schedule
                      </Button>
                    </Grid>
                    {formData.schedules.length >= 2 && (
                      <Grid item xs={12}>
                        <FormControl fullWidth sx={{ minWidth: 280 }}>
                          <InputLabel>When multiple schedules</InputLabel>
                          <Select
                            value={formData.scheduleCombinationMode}
                            label="When multiple schedules"
                            onChange={(e) => setFormData({ ...formData, scheduleCombinationMode: e.target.value })}
                          >
                            <MenuItem value="WHICHEVER_OCCURS_FIRST">Whichever occurs first</MenuItem>
                            <MenuItem value="ALL_MUST_BE_MET">All must be met</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Last performed at (optional) – shown when at least one schedule row is valid */}
          {(formData.inspectionCategory === 'ONE_TIME_INSPECTIONS' || formData.recurrenceType === 'RECURRING') &&
            formData.schedules.some(isScheduleRowValid) && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2.5 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                  Last performed at
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Optional. When this was last carried out (e.g. for used aircraft). Same unit as each row above.
                  {schedule
                    ? ' You can upload a source document (PDF, DOC/DOCX, image, or text) per row; use Download to open it.'
                    : ' After you save the schedule, you can attach a source document when editing.'}
                </Typography>
                <Grid container spacing={2}>
                  {formData.schedules.map((entry, index) =>
                    isScheduleRowValid(entry) ? (
                      <Grid container spacing={2} key={`lastdone-${index}`} alignItems="center" item xs={12}>
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" color="text.secondary">
                            {entry.recurrenceBasedOn === 'ON_CONDITION'
                              ? 'On Condition'
                              : `${entry.recurrenceBasedOn} ${entry.matchType === 'BETWEEN' ? `${entry.recurrenceValue}–${entry.recurrenceValue2}` : entry.recurrenceValue}`}
                          </Typography>
                        </Grid>
                        {entry.recurrenceBasedOn === 'HOURS' ? (
                          <>
                            <Grid item xs={12} md={2}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Last performed at (hours)"
                                type="number"
                                value={entry.lastDoneValue === '' || entry.lastDoneValue == null ? '' : (Number.isNaN(Number(entry.lastDoneValue)) ? '' : Math.floor(Number(entry.lastDoneValue)))}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const currentMinutes = (entry.lastDoneValue !== '' && entry.lastDoneValue != null && !Number.isNaN(Number(entry.lastDoneValue)))
                                    ? Math.min(59, Math.max(0, Math.round((Number(entry.lastDoneValue) % 1) * 60)))
                                    : 0;
                                  if (v === '') {
                                    handleScheduleChange(index, 'lastDoneValue', currentMinutes === 0 ? '' : currentMinutes / 60);
                                    return;
                                  }
                                  const num = parseInt(v, 10);
                                  if (!Number.isNaN(num) && num >= 0 && num <= 99999999) {
                                    const newDecimal = num + currentMinutes / 60;
                                    handleScheduleChange(index, 'lastDoneValue', newDecimal);
                                  }
                                }}
                                placeholder="e.g. 950"
                                inputProps={{ min: 0, max: 99999999, step: 1 }}
                              />
                            </Grid>
                            <Grid item xs={12} md={2} sx={{ minWidth: 100 }}>
                              <Tooltip
                                title="Minutes (0–59) of the hour when maintenance was last performed. Stored with hours as decimal hours (e.g. 950.5 for 950h 30m)."
                                placement="top"
                                arrow
                              >
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Last performed at (minutes)"
                                  type="number"
                                  value={entry.lastDoneValue === '' || entry.lastDoneValue == null ? '' : (Number.isNaN(Number(entry.lastDoneValue)) ? '' : Math.min(59, Math.max(0, Math.round((Number(entry.lastDoneValue) % 1) * 60))))}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    const currentHours = (entry.lastDoneValue !== '' && entry.lastDoneValue != null && !Number.isNaN(Number(entry.lastDoneValue)))
                                      ? Math.floor(Number(entry.lastDoneValue))
                                      : 0;
                                    if (v === '') {
                                      handleScheduleChange(index, 'lastDoneValue', currentHours === 0 ? '' : currentHours);
                                      return;
                                    }
                                    const num = parseInt(v, 10);
                                    if (!Number.isNaN(num) && num >= 0 && num <= 59) {
                                      const newDecimal = currentHours + num / 60;
                                      handleScheduleChange(index, 'lastDoneValue', newDecimal);
                                    }
                                  }}
                                  placeholder="0–59"
                                  inputProps={{ min: 0, max: 59, step: 1 }}
                                  sx={{ minWidth: 100 }}
                                />
                              </Tooltip>
                            </Grid>
                            <Grid item xs={12} md={3} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                {formatLastDoneHoursDisplay(entry.lastDoneValue) || '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {entry.lastDoneValue !== '' && entry.lastDoneValue != null && !Number.isNaN(Number(entry.lastDoneValue))
                                  ? `Stored as: ${Number(Number(entry.lastDoneValue).toFixed(4))}`
                                  : 'Stored as: —'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Note (optional)"
                                value={entry.lastDoneNote == null ? '' : entry.lastDoneNote}
                                onChange={(e) => handleScheduleChange(index, 'lastDoneNote', e.target.value)}
                                placeholder="Optional note"
                              />
                            </Grid>
                          </>
                        ) : entry.recurrenceBasedOn === 'CYCLES' ? (
                          <>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Last performed at (cycles)"
                                value={entry.lastDoneValue === '' ? '' : entry.lastDoneValue}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') { handleScheduleChange(index, 'lastDoneValue', ''); return; }
                                  const allowed = raw.replace(/[^0-9.]/g, '');
                                  const parts = allowed.split('.');
                                  const intPart = (parts[0] || '').slice(0, 8);
                                  const decPart = parts.length > 1 ? (parts.slice(1).join('').slice(0, 3)) : '';
                                  handleScheduleChange(index, 'lastDoneValue', decPart ? `${intPart}.${decPart}` : intPart);
                                }}
                                placeholder="e.g. 123456.123"
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Note (optional)"
                                value={entry.lastDoneNote == null ? '' : entry.lastDoneNote}
                                onChange={(e) => handleScheduleChange(index, 'lastDoneNote', e.target.value)}
                                placeholder="Optional note"
                              />
                            </Grid>
                          </>
                        ) : entry.recurrenceBasedOn === 'LANDINGS' ? (
                          <>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Last performed at (landings)"
                                type="number"
                                value={entry.lastDoneValue === '' || entry.lastDoneValue == null ? '' : (Number.isNaN(Number(entry.lastDoneValue)) ? '' : Math.floor(Number(entry.lastDoneValue)))}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v === '') { handleScheduleChange(index, 'lastDoneValue', ''); return; }
                                  const num = parseInt(v, 10);
                                  if (!Number.isNaN(num) && num >= 0) handleScheduleChange(index, 'lastDoneValue', num);
                                }}
                                placeholder="e.g. 5000"
                                inputProps={{ min: 0, step: 1 }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Note (optional)"
                                value={entry.lastDoneNote == null ? '' : entry.lastDoneNote}
                                onChange={(e) => handleScheduleChange(index, 'lastDoneNote', e.target.value)}
                                placeholder="Optional note"
                              />
                            </Grid>
                          </>
                        ) : (['DAYS', 'MONTHS', 'YEARS', 'ON_CONDITION'].includes(entry.recurrenceBasedOn)) ? (
                          <>
                            <Grid item xs={12} md={3}>
                              <TextField
                                fullWidth
                                size="small"
                                label={displayTimezone ? `Last performed at (date) (${getTimezoneLabel(displayTimezone)})` : 'Last performed at (date)'}
                                type="date"
                                value={entry.lastDoneValue == null || entry.lastDoneValue === '' ? '' : (typeof entry.lastDoneValue === 'string' && entry.lastDoneValue.length >= 10 ? entry.lastDoneValue.slice(0, 10) : entry.lastDoneValue)}
                                onChange={(e) => handleScheduleChange(index, 'lastDoneValue', e.target.value || '')}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Note (optional)"
                                value={entry.lastDoneNote == null ? '' : entry.lastDoneNote}
                                onChange={(e) => handleScheduleChange(index, 'lastDoneNote', e.target.value)}
                                placeholder="Optional note"
                              />
                            </Grid>
                          </>
                        ) : null}
                        {schedule ? (
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 1,
                                pt: 0.5,
                                borderTop: 1,
                                borderColor: 'divider'
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                Source document
                              </Typography>
                              <input
                                id={`amp-last-performed-doc-${index}`}
                                type="file"
                                style={{ display: 'none' }}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,text/plain"
                                disabled={loading || lastPerformedDocBusyIndex === index}
                                onChange={(e) => {
                                  handleLastPerformedSourceUpload(index, e.target.files);
                                  e.target.value = '';
                                }}
                              />
                              <Button
                                component="label"
                                htmlFor={`amp-last-performed-doc-${index}`}
                                size="small"
                                variant="outlined"
                                startIcon={<AttachFileIcon />}
                                disabled={loading || lastPerformedDocBusyIndex === index}
                              >
                                Upload
                              </Button>
                              {entry.lastDoneSourceDocument?.fileUrl ? (
                                <>
                                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 220 }} noWrap title={entry.lastDoneSourceDocument.originalFileName || ''}>
                                    {entry.lastDoneSourceDocument.originalFileName || 'Attached file'}
                                  </Typography>
                                  <Button
                                    size="small"
                                    href={entry.lastDoneSourceDocument.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Download
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="text"
                                    disabled={loading || lastPerformedDocBusyIndex === index}
                                    onClick={() => handleLastPerformedSourceRemove(index)}
                                  >
                                    Remove
                                  </Button>
                                </>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  No file attached
                                </Typography>
                              )}
                            </Box>
                          </Grid>
                        ) : (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Save the schedule once to attach a source document here.
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    ) : null
                  )}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* AMP Checks Section */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2.5 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                AMP Checks
              </Typography>
              <AMPChecksManager
                checks={formData.ampChecks}
                onChange={(checks) => setFormData({ ...formData, ampChecks: checks })}
              />
            </Paper>
          </Grid>

          {schedule && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                  Proof documents
                </Typography>
                {schedule.proofDocuments && schedule.proofDocuments.length > 0 && (
                  <List dense sx={{ mb: 1 }}>
                    {schedule.proofDocuments.map((p, i) => (
                      <ListItem key={i}>
                        <ListItemText primary={p.originalFileName || p.fileName} />
                        <ListItemSecondaryAction>
                          <Button
                            size="small"
                            startIcon={<FileDownloadIcon fontSize="small" />}
                            component="a"
                            href={p.fileUrl}
                            download={p.originalFileName || p.fileName}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download
                          </Button>
                          <Button
                            size="small"
                            href={p.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ ml: 0.5 }}
                          >
                                                        Open
                          </Button>
                          {canUploadProof && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleProofDelete(p.fileName)}
                              disabled={deletingProof === p.fileName}
                              aria-label="Remove proof document"
                              sx={{ ml: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}
                {canUploadProof && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      component="label"
                      startIcon={<AttachFile />}
                      disabled={uploadingProof}
                    >
                      {uploadingProof ? 'Uploading...' : 'Upload proof document'}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
                        onChange={handleProofUpload}
                      />
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
        )}
        {schedule && tabIndex === 1 && (
          <Box sx={{ mt: 2 }}>
            {historyLoading && (
              <Typography variant="body2" color="text.secondary">Loading history...</Typography>
            )}
            {historyError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHistoryError(null)}>
                {historyError}
              </Alert>
            )}
            {!historyLoading && !historyError && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                      Audit timeline
                    </Typography>
                    {historyEvents.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No history records.</Typography>
                    ) : (
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {historyEvents.map((ev) => (
                          <Box component="li" key={ev.id} sx={{ mb: 1.5 }}>
                            <Typography variant="body2">
                              <strong>{ev.action}</strong>
                              {ev.date && ` — ${new Date(ev.date).toLocaleString()}`}
                              {ev.who && ` by ${ev.who.firstName || ''} ${ev.who.lastName || ''}`.trim()}
                            </Typography>
                            {ev.summary && ev.summary.length > 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                                {ev.summary.join('; ')}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                      Intimations
                    </Typography>
                    {historyIntimations.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No intimations linked to this AMP schedule.</Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Intimation ID</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Created</TableCell>
                              <TableCell>Fulfilled by</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {historyIntimations.map((int) => (
                              <TableRow key={int._id}>
                                <TableCell sx={{ fontFamily: 'monospace' }}>#{String(int._id).slice(-6)}</TableCell>
                                <TableCell><Chip label={int.status || '—'} size="small" /></TableCell>
                                <TableCell>{int.createdAt ? new Date(int.createdAt).toLocaleDateString() : '—'}</TableCell>
                                <TableCell>
                                  {int.workOrderNumber || (int.linkedWorkOrderId?.workOrderNumber)
                                    ? `WO #${int.workOrderNumber || int.linkedWorkOrderId?.workOrderNumber}`
                                    : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                      Work orders created from this AMP
                    </Typography>
                    {!schedule.workOrders || schedule.workOrders.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No work orders created from this schedule yet.</Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Work order #</TableCell>
                              <TableCell>Title</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Created</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {schedule.workOrders.map((wo) => (
                              <TableRow key={wo.workOrderId?._id || wo.workOrderId}>
                                <TableCell sx={{ fontFamily: 'monospace' }}>
                                  {wo.workOrderId?.workOrderNumber ? `#${wo.workOrderId.workOrderNumber}` : '—'}
                                </TableCell>
                                <TableCell>{wo.workOrderId?.title || '—'}</TableCell>
                                <TableCell><Chip label={wo.workOrderId?.status || '—'} size="small" /></TableCell>
                                <TableCell>{wo.createdAt ? new Date(wo.createdAt).toLocaleDateString() : '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {(tabIndex === 0 || !schedule) && (
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            loading ||
            !formData.aircraftId ||
            !formData.title.trim() ||
            !formData.inspectionCategory ||
            formData.ampChecks.length === 0 ||
            !formData.recurrenceType ||
            ((formData.recurrenceType === 'RECURRING' || formData.inspectionCategory === 'ONE_TIME_INSPECTIONS') &&
              (formData.schedules.length === 0 ||
                !formData.schedules.every(s => {
                  if (!s.recurrenceBasedOn) return false;
                  if (s.recurrenceBasedOn === 'ON_CONDITION') return true;
                  const hasVal1 = s.recurrenceValue !== '' && s.recurrenceValue != null;
                  if ((s.matchType || 'EXACTLY') === 'BETWEEN') return hasVal1 && (s.recurrenceValue2 !== '' && s.recurrenceValue2 != null);
                  return hasVal1;
                })))
          }
        >
          {loading ? 'Saving...' : schedule ? 'Update' : 'Create'}
        </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditAMPScheduleDialog;
